/**
 * Forecasting Page for HMH Lunch Ordering System
 *
 * Displays demand forecasts and historical patterns for kitchen planning.
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../../components/auth';
import {
  useKitchenPrep,
  useWeekForecast,
  useHistoricalPatterns,
  useForecastAccuracy,
} from '../../hooks/useForecast';

type Tab = 'kitchen' | 'week' | 'patterns' | 'accuracy';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function ForecastingPage() {
  const { accessToken } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('kitchen');
  const [accuracyDays, setAccuracyDays] = useState(14);

  const tabs = [
    { id: 'kitchen' as Tab, label: 'Kitchen Prep' },
    { id: 'week' as Tab, label: 'Weekly Forecast' },
    { id: 'patterns' as Tab, label: 'Historical Patterns' },
    { id: 'accuracy' as Tab, label: 'Accuracy Tracking' },
  ];

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-4 px-6 text-center font-medium text-sm border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600 bg-blue-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {activeTab === 'kitchen' && <KitchenPrepTab accessToken={accessToken} />}
        {activeTab === 'week' && <WeeklyForecastTab accessToken={accessToken} />}
        {activeTab === 'patterns' && <PatternsTab accessToken={accessToken} />}
        {activeTab === 'accuracy' && (
          <AccuracyTab
            accessToken={accessToken}
            days={accuracyDays}
            onDaysChange={setAccuracyDays}
          />
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Kitchen Prep Tab
// =============================================================================

function KitchenPrepTab({ accessToken }: { accessToken: string | null }) {
  const { data, fetchKitchenPrep, loading, error } = useKitchenPrep();

  useEffect(() => {
    if (accessToken) {
      fetchKitchenPrep(accessToken);
    }
  }, [accessToken, fetchKitchenPrep]);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;
  if (!data) return <p className="text-gray-500">No forecast data available.</p>;

  const total = data.total_predicted;
  const hasData = total > 0;

  return (
    <div>
      <h2 className="text-xl font-bold text-gray-900 mb-4">
        Tomorrow's Kitchen Prep - {data.day_name}, {new Date(data.date).toLocaleDateString()}
      </h2>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-600 font-medium">Total Predicted</p>
          <p className="text-3xl font-bold text-blue-800">{data.total_predicted}</p>
        </div>
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
          <p className="text-sm text-indigo-600 font-medium">Regular Lunches</p>
          <p className="text-3xl font-bold text-indigo-800">{data.forecasts.regular}</p>
          {data.regular_menu_item && (
            <p className="text-xs text-indigo-500 mt-1 truncate">{data.regular_menu_item}</p>
          )}
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-sm text-green-600 font-medium">Salads</p>
          <p className="text-3xl font-bold text-green-800">{data.forecasts.salad}</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-sm text-amber-600 font-medium">Specials</p>
          <p className="text-3xl font-bold text-amber-800">{data.forecasts.special}</p>
        </div>
      </div>

      {/* Distribution and Recommendations */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Order Distribution */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-lg font-semibold mb-4">Order Distribution</h3>
          {hasData ? (
            <div className="space-y-3">
              <ProgressBar
                label="Regular"
                value={data.forecasts.regular}
                max={total}
                color="bg-indigo-600"
              />
              <ProgressBar
                label="Salad"
                value={data.forecasts.salad}
                max={total}
                color="bg-green-600"
              />
              <ProgressBar
                label="Special"
                value={data.forecasts.special}
                max={total}
                color="bg-amber-600"
              />
            </div>
          ) : (
            <p className="text-gray-500">No historical data available yet.</p>
          )}
        </div>

        {/* Prep Recommendations */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-yellow-800 mb-4">Prep Recommendations</h3>
          {data.prep_recommendations.length > 0 ? (
            <ul className="space-y-2">
              {data.prep_recommendations.map((rec, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <span className="text-yellow-600">&#8226;</span>
                  <span className="text-gray-700">{rec}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-600">Standard prep quantities recommended.</p>
          )}
          <div className="mt-4 pt-4 border-t border-yellow-200">
            <p className="text-sm text-gray-600">
              Menu Week: <span className="font-semibold">{data.menu_week}</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Weekly Forecast Tab
// =============================================================================

function WeeklyForecastTab({ accessToken }: { accessToken: string | null }) {
  const { data, fetchWeekForecast, loading, error } = useWeekForecast();

  useEffect(() => {
    if (accessToken) {
      fetchWeekForecast(accessToken);
    }
  }, [accessToken, fetchWeekForecast]);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;
  if (!data || data.length === 0) return <p className="text-gray-500">No forecast data available.</p>;

  return (
    <div>
      <h2 className="text-xl font-bold text-gray-900 mb-4">7-Day Forecast</h2>

      {/* Visual Bar Summary */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <h3 className="text-lg font-semibold mb-4">Daily Totals</h3>
        <div className="space-y-3">
          {data.map((forecast) => {
            const dayName = new Date(forecast.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
            const maxTotal = Math.max(...data.map(d => d.total_predicted), 1);
            return (
              <div key={forecast.date} className="flex items-center gap-4">
                <span className="w-32 text-sm font-medium text-gray-700">{dayName}</span>
                <div className="flex-1 flex gap-1 h-6">
                  <div
                    className="bg-indigo-600 rounded-l"
                    style={{ width: `${(forecast.forecasts.regular.predicted_count / maxTotal) * 100}%` }}
                    title={`Regular: ${forecast.forecasts.regular.predicted_count}`}
                  />
                  <div
                    className="bg-green-600"
                    style={{ width: `${(forecast.forecasts.salad.predicted_count / maxTotal) * 100}%` }}
                    title={`Salad: ${forecast.forecasts.salad.predicted_count}`}
                  />
                  <div
                    className="bg-amber-600 rounded-r"
                    style={{ width: `${(forecast.forecasts.special.predicted_count / maxTotal) * 100}%` }}
                    title={`Special: ${forecast.forecasts.special.predicted_count}`}
                  />
                </div>
                <span className="w-12 text-sm font-bold text-gray-900 text-right">{forecast.total_predicted}</span>
              </div>
            );
          })}
        </div>
        <div className="flex gap-4 mt-4 text-xs">
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-indigo-600 rounded"></span> Regular</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-600 rounded"></span> Salad</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-amber-600 rounded"></span> Special</span>
        </div>
      </div>

      {/* Daily Details Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Week</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Regular</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Salad</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Special</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((forecast) => (
              <tr key={forecast.date}>
                <td className="px-4 py-3 text-sm font-medium text-gray-900">
                  {new Date(forecast.date).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">Week {forecast.menu_week}</td>
                <td className="px-4 py-3 text-sm text-right text-gray-900">{forecast.forecasts.regular.predicted_count}</td>
                <td className="px-4 py-3 text-sm text-right text-gray-900">{forecast.forecasts.salad.predicted_count}</td>
                <td className="px-4 py-3 text-sm text-right text-gray-900">{forecast.forecasts.special.predicted_count}</td>
                <td className="px-4 py-3 text-sm text-right font-bold text-gray-900">{forecast.total_predicted}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =============================================================================
// Patterns Tab
// =============================================================================

function PatternsTab({ accessToken }: { accessToken: string | null }) {
  const { data, fetchPatterns, loading, error } = useHistoricalPatterns();

  useEffect(() => {
    if (accessToken) {
      fetchPatterns(accessToken);
    }
  }, [accessToken, fetchPatterns]);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;
  if (!data) return <p className="text-gray-500">No pattern data available.</p>;

  const hasData = data.data_range.total_days > 0;

  return (
    <div>
      <h2 className="text-xl font-bold text-gray-900 mb-4">Historical Order Patterns</h2>

      {/* Data Range Info */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <p className="text-sm text-gray-600">
          {hasData ? (
            <>Based on {data.data_range.total_days} days of data ({data.data_range.start} to {data.data_range.end})</>
          ) : (
            <>No historical data available yet. Patterns will appear after orders are placed.</>
          )}
        </p>
      </div>

      {hasData && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Day of Week Pattern */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-semibold mb-4">Average Orders by Day of Week</h3>
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="py-2 text-left text-sm font-medium text-gray-500">Day</th>
                  <th className="py-2 text-right text-sm font-medium text-gray-500">Regular</th>
                  <th className="py-2 text-right text-sm font-medium text-gray-500">Salad</th>
                  <th className="py-2 text-right text-sm font-medium text-gray-500">Special</th>
                  <th className="py-2 text-right text-sm font-medium text-gray-500">Total</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(data.day_of_week_patterns).map(([dow, values]) => {
                  const total = values.regular + values.salad + values.special;
                  return (
                    <tr key={dow} className="border-b border-gray-100">
                      <td className="py-2 text-sm font-medium text-gray-900">{DAY_NAMES[Number(dow)]}</td>
                      <td className="py-2 text-sm text-right text-gray-900">{Math.round(values.regular)}</td>
                      <td className="py-2 text-sm text-right text-gray-900">{Math.round(values.salad)}</td>
                      <td className="py-2 text-sm text-right text-gray-900">{Math.round(values.special)}</td>
                      <td className="py-2 text-sm text-right font-bold text-gray-900">{Math.round(total)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Menu Week Pattern */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-semibold mb-4">Average Daily Orders by Menu Week</h3>
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="py-2 text-left text-sm font-medium text-gray-500">Week</th>
                  <th className="py-2 text-right text-sm font-medium text-gray-500">Avg Daily</th>
                  <th className="py-2 text-right text-sm font-medium text-gray-500">Total Orders</th>
                  <th className="py-2 text-right text-sm font-medium text-gray-500">Days</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(data.menu_week_patterns).map(([week, values]) => (
                  <tr key={week} className="border-b border-gray-100">
                    <td className="py-2 text-sm font-medium text-gray-900">Week {week}</td>
                    <td className="py-2 text-sm text-right text-gray-900">{Math.round(values.avg_daily)}</td>
                    <td className="py-2 text-sm text-right text-gray-900">{values.total_orders}</td>
                    <td className="py-2 text-sm text-right text-gray-500">{values.days_with_data}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Accuracy Tab
// =============================================================================

interface AccuracyTabProps {
  accessToken: string | null;
  days: number;
  onDaysChange: (days: number) => void;
}

function AccuracyTab({ accessToken, days, onDaysChange }: AccuracyTabProps) {
  const { data, fetchAccuracy, loading, error } = useForecastAccuracy();

  useEffect(() => {
    if (accessToken) {
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      fetchAccuracy(accessToken, startDate, endDate);
    }
  }, [accessToken, days, fetchAccuracy]);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-gray-900">Forecast Accuracy</h2>
        <select
          value={days}
          onChange={(e) => onDaysChange(Number(e.target.value))}
          className="border border-gray-300 rounded-md px-3 py-2"
        >
          <option value={7}>Last 7 days</option>
          <option value={14}>Last 14 days</option>
          <option value={30}>Last 30 days</option>
        </select>
      </div>

      {/* Accuracy Summary */}
      {data?.summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-600 font-medium">Mean Absolute Error</p>
            <p className="text-3xl font-bold text-blue-800">{data.summary.mean_absolute_error}</p>
            <p className="text-xs text-blue-500">orders per day</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-sm text-green-600 font-medium">Mean % Error</p>
            <p className="text-3xl font-bold text-green-800">{data.summary.mean_percentage_error}%</p>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600 font-medium">Forecasts Analyzed</p>
            <p className="text-3xl font-bold text-gray-800">{data.summary.total_forecasts}</p>
          </div>
        </div>
      )}

      {/* Accuracy Table */}
      {data?.accuracy_records && data.accuracy_records.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Predicted</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actual</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Error</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">% Error</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.accuracy_records.map((record, idx) => (
                <tr key={idx} className={record.percentage_error > 20 ? 'bg-red-50' : ''}>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {new Date(record.date).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 capitalize">{record.order_type}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-900">{record.predicted}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-900">{record.actual}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-900">{record.absolute_error}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-900">{record.percentage_error}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-gray-50 rounded-lg p-8 text-center">
          <p className="text-gray-500">
            No accuracy data available yet. Forecasts will be compared to actuals after they occur.
          </p>
          <p className="text-sm text-gray-400 mt-2">
            Use the "Save Forecast" feature to store predictions for later accuracy tracking.
          </p>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Helper Components
// =============================================================================

function LoadingSpinner() {
  return (
    <div className="flex justify-center items-center py-12">
      <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
      </svg>
    </div>
  );
}

function ErrorMessage({ message }: { message: string }) {
  return (
    <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4">
      {message}
    </div>
  );
}

interface ProgressBarProps {
  label: string;
  value: number;
  max: number;
  color: string;
}

function ProgressBar({ label, value, max, color }: ProgressBarProps) {
  const percentage = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="w-16 text-sm font-medium text-gray-700">{label}</span>
      <div className="flex-1 bg-gray-200 rounded-full h-4 overflow-hidden">
        <div className={`${color} h-full rounded-full`} style={{ width: `${percentage}%` }} />
      </div>
      <span className="w-8 text-sm font-bold text-gray-900 text-right">{value}</span>
    </div>
  );
}
